/**
 * Navbar Component
 * Top navigation bar with user profile and notifications
 * Spans full width of screen with tenant system name
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getContrastText } from '../../utils/themeGenerator';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Business as TenantIcon,
  SwapHoriz as SwitchIcon,
  Check as CheckIcon,
  Storage as DatabaseIcon,
  Email as EmailIcon,
  Cloud as BlobIcon,
  Schedule as JobsIcon,
  CheckCircle as HealthyIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import { apiGet, apiPost, setSelectedTenantId } from '../../utils/apiClient';
import logger from '../../utils/logger';

function Navbar({ handleDrawerToggle, sidebarWidth = 0 }) {
  const muiTheme = useTheme();
  const { user, logout, isAdmin } = usePermissions();
  const { tenant, switchTenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [serviceStatusLoading, setServiceStatusLoading] = useState(false);

  // Get tenant details
  const systemName = tenant?.systemName || 'Capital Management System';
  const tenantName = tenant?.name || null;
  const tenantLogo = tenant?.theme?.logoUrl || tenant?.logoUrl || null;

  // Load available tenants for multi-tenant users
  // Auto-select if user only has access to one tenant
  // Redirect to no-access page if user has no tenant assignments
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const response = await apiGet('/tenants/my-tenants');
        if (response.success && response.data) {
          // Sort tenants: regular tenants first (alphabetically), master/default tenant last
          const sortedTenants = [...response.data].sort((a, b) => {
            const aIsMaster = a.code === 'master' || a.code === 'default';
            const bIsMaster = b.code === 'master' || b.code === 'default';
            if (aIsMaster && !bIsMaster) return 1;  // a goes after b
            if (!aIsMaster && bIsMaster) return -1; // a goes before b
            return a.name.localeCompare(b.name);    // alphabetical
          });
          setAvailableTenants(sortedTenants);

          // If user has NO tenants assigned, redirect to no-access page
          if (response.data.length === 0 && location.pathname !== '/no-access') {
            navigate('/no-access');
            return;
          }

          // Check if currently selected tenant is still valid (user still has access to it).
          // Only invalidate the selection if the response is non-empty and the id is missing.
          // An empty response can happen when the API has no tenant context yet (eg. the very
          // first request after login resolved against the wrong tenant) — clearing localStorage
          // in that case caused a destructive reload loop, see prior bug.
          const currentSelectedId = localStorage.getItem('selectedTenantId');
          if (currentSelectedId && response.data.length > 0) {
            const isValidTenant = response.data.some(t => String(t.id) === currentSelectedId);
            if (!isValidTenant) {
              localStorage.removeItem('selectedTenantId');
              // If user has only one tenant, auto-select it
              if (response.data.length === 1) {
                const onlyTenant = response.data[0];
                setSelectedTenantId(onlyTenant.id);
              }
              window.location.reload();
              return;
            }
          }

          // Auto-select if user only has access to ONE tenant and no tenant is currently selected
          if (response.data.length === 1 && !currentSelectedId) {
            const onlyTenant = response.data[0];
            // Only auto-switch if we're not already on this tenant
            if (response.currentTenantId !== onlyTenant.id) {
              setSelectedTenantId(onlyTenant.id);
              window.location.reload();
            }
          }
        }
      } catch (err) {
        logger.warn('Could not load tenant list', { message: err.message });
      }
    };
    if (user) {
      loadTenants();
    }
  }, [user, navigate, location.pathname]);

  // Load service status summary for navbar (only for admins)
  useEffect(() => {
    const loadServiceStatus = async () => {
      try {
        setServiceStatusLoading(true);
        const response = await apiGet('/admin/service-status/summary');
        if (response.success) {
          setServiceStatus(response);
        }
      } catch (err) {
        // Silently ignore - user may not have admin access
        logger.debug('Could not load service status', { message: err.message });
      } finally {
        setServiceStatusLoading(false);
      }
    };

    // Only load if user has admin permissions (use role-based isAdmin flag)
    if (isAdmin) {
      loadServiceStatus();
      // Refresh every 60 seconds
      const interval = setInterval(loadServiceStatus, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const handleTenantMenuOpen = (event) => {
    if (availableTenants.length > 1) {
      setTenantMenuAnchor(event.currentTarget);
    }
  };

  const handleTenantMenuClose = () => {
    setTenantMenuAnchor(null);
  };

  const handleTenantSwitch = async (tenantId) => {
    try {
      const response = await apiPost('/tenants/switch', { tenantId });
      if (response.success) {
        // Store selection in localStorage - will be sent as X-Tenant-ID header
        setSelectedTenantId(tenantId);
        // Navigate to home (not the current path) so MasterTenantHomeRedirect
        // routes to the right landing page for the new tenant: master/admin
        // lands on Service Status (master has no dashboard), real tenants land
        // on the Dashboard. A full document load is required because tenant
        // context, theme, and permissions are bootstrapped at app start.
        window.location.assign('/');
      }
    } catch (err) {
      logger.error('Failed to switch tenant', { message: err.message });
    }
    handleTenantMenuClose();
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotifications = (event) => {
    setNotifAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNotifAnchor(null);
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleLogout = () => {
    handleClose();
    logout();
    // Navigate to login page
    navigate('/login');
  };

  const handleServiceStatusClick = () => {
    handleClose();
    navigate('/admin/service-status');
  };

  // Helper functions for service status
  const getServiceStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <HealthyIcon fontSize="small" sx={{ color: 'success.main' }} />;
      case 'unhealthy':
        return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
      case 'degraded':
      case 'not_configured':
        return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      default:
        return <UnknownIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  const getServiceIcon = (serviceName) => {
    switch (serviceName) {
      case 'database':
        return <DatabaseIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
      case 'email':
        return <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
      case 'blob':
        return <BlobIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
      case 'jobs':
        return <JobsIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
      default:
        return <UnknownIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  const getServiceLabel = (serviceName) => {
    switch (serviceName) {
      case 'database': return 'Database';
      case 'email': return 'Email';
      case 'blob': return 'Blob';
      case 'jobs': return 'Jobs';
      default: return serviceName;
    }
  };

  const headerBg = muiTheme.palette.background.header || '#0f172a';
  const headerTextColor = useMemo(() => getContrastText(headerBg), [headerBg]);

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - ${sidebarWidth}px)` },
        ml: { sm: `${sidebarWidth}px` },
        backgroundColor: headerBg,
        color: headerTextColor,
        transition: (theme) =>
          theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Tenant Logo */}
        {tenantLogo && (
          <Box
            component="img"
            src={tenantLogo}
            alt={tenantName || 'Logo'}
            sx={{
              height: 36,
              maxWidth: 120,
              mr: 2,
              objectFit: 'contain',
            }}
          />
        )}

        {/* System Name */}
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          {systemName}
        </Typography>

        {/* Tenant Indicator - shows which tenant user is logged into */}
        {tenantName && (
          <Chip
            icon={<TenantIcon />}
            label={tenantName}
            size="small"
            onClick={handleTenantMenuOpen}
            deleteIcon={availableTenants.length > 1 ? <SwitchIcon /> : undefined}
            onDelete={availableTenants.length > 1 ? handleTenantMenuOpen : undefined}
            sx={{
              mr: 2,
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'inherit',
              cursor: availableTenants.length > 1 ? 'pointer' : 'default',
              '& .MuiChip-icon': {
                color: 'inherit',
              },
              '& .MuiChip-deleteIcon': {
                color: 'inherit',
              },
              display: { xs: 'none', sm: 'flex' },
            }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          <IconButton
            size="large"
            aria-label="show notifications"
            color="inherit"
            onClick={handleNotifications}
          >
            <Badge badgeContent={0} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Profile */}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>

          <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
            {user?.email || 'User'}
          </Typography>
        </Box>

        {/* User Menu */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </MenuItem>
          {tenantName && (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TenantIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {tenantName}
                </Typography>
              </Box>
            </MenuItem>
          )}
          <Divider />
          {/* Service Status Summary */}
          {serviceStatus && serviceStatus.services && [
            <MenuItem key="svc-header" disabled sx={{ py: 0.5, minHeight: 32 }}>
              <Typography variant="caption" color="text.secondary">
                Services
              </Typography>
            </MenuItem>,
            ...serviceStatus.services.map((service) => (
              <MenuItem
                key={service.name}
                onClick={handleServiceStatusClick}
                sx={{ py: 0.5, minHeight: 36 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {getServiceStatusIcon(service.status)}
                  {getServiceIcon(service.name)}
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {getServiceLabel(service.name)}:
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {service.identifier}
                  </Typography>
                </Box>
              </MenuItem>
            )),
            <Divider key="svc-divider" />,
          ]}
          {serviceStatusLoading && [
            <MenuItem key="svc-loading" disabled sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading services...
                </Typography>
              </Box>
            </MenuItem>,
            <Divider key="svc-loading-divider" />,
          ]}
          <MenuItem onClick={handleSettings}>Settings</MenuItem>
          <MenuItem onClick={handleLogout}>
            Logout
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          id="notifications-menu"
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={handleClose}
        >
          <MenuItem>
            <Typography variant="body2" color="text.secondary">
              No new notifications
            </Typography>
          </MenuItem>
        </Menu>

        {/* Tenant Switcher Menu */}
        <Menu
          id="tenant-menu"
          anchorEl={tenantMenuAnchor}
          open={Boolean(tenantMenuAnchor)}
          onClose={handleTenantMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem disabled>
            <Typography variant="caption" color="text.secondary">
              Switch Tenant
            </Typography>
          </MenuItem>
          <Divider />
          {availableTenants.map((t) => (
            <MenuItem
              key={t.id}
              onClick={() => handleTenantSwitch(t.id)}
              selected={t.id === tenant?.id}
            >
              <ListItemIcon>
                {t.logoUrl ? (
                  <Avatar src={t.logoUrl} sx={{ width: 24, height: 24 }} />
                ) : (
                  <TenantIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={t.name}
                secondary={t.systemName !== t.name ? t.systemName : null}
              />
              {t.id === tenant?.id && (
                <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
              )}
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
